import React, { useEffect, useState } from 'react';
import ContentLayout from '@layouts/ContentLayout';
import { Box, Button, Divider, Flex, Grid, HStack, Select, Spinner, Text, VStack } from '@chakra-ui/react';
import { MdEast, MdSearch } from 'react-icons/md';
import InputComponent from '@components/Input';
import { RFI_REQUEST_REASONS, RFI_STATUS, RFI_TYPES, SORT_OPTIONS } from './constants';
import { getPendingForHoursOptions } from './utils';
import useDebounce from '@hooks/useDebounce';
import { useDispatch, useSelector } from 'react-redux';
import { getRFIs } from '@store/actions/rfiActions';
import { RFI_CONSTANTS as types } from '@store/constants';
import Pagination from '@components/Pagination';
import GenericCard from '@components/GenericCard';
import { CARD_TYPE, COUNTRY_CODE } from '@constants';
import { useNavigate } from 'react-router-dom';
import useIsMobile from '@hooks/useIsMobile';
import OrderDetailsCard from '@components/OrderDetailsCard';

const RFITransactions = () => {
  const rfis = useSelector((state) => state.rfiDetails.rfis);
  const [filters, setFilters] = useState({
    countryCode: '',
    status: RFI_STATUS.SUBMITTED,
    sortKey: 'created_at',
    sortValue: 'ASC',
    pendingForHours: 96,
  });
  const [searchValue, setSearchValue] = useState(null);
  const [page, setPage] = useState(rfis.page);
  const [showOrder, setShowOrder] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState();
  const debounce = useDebounce(searchValue, 500);
  const disptach = useDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const parsedValue = name === 'pendingForHours' && value ? parseInt(value, 10) : value;

    setFilters((prev) => {
      const updated = { ...prev };
      if (parsedValue && parsedValue !== '') {
        updated[name] = parsedValue;
      } else if (name in updated) {
        delete updated[name];
      }
      return updated;
    });

    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setPage(0);
  };

  useEffect(() => {
    if (debounce != null) setFilters((prev) => ({ ...prev, query: debounce }));
  }, [debounce]);

  useEffect(() => {
    if (filters || rfis.page >= 0) {
      handleSearch();
    }
  }, [filters, rfis.page]);

  const handleSearch = () => {
    disptach(
      getRFIs({
        reqData: { ...filters, pageSize: rfis.pageSize, page: page },
        onSuccess: (data) => {
          disptach({
            type: types.GET_RFI_ORDERS_SUCCESS,
            payload: { data }
          });
        }
      })
    );
  };

  const handlePageChange = (page) => {
    disptach({
      type: types.UPDATE_RFI_PAGE_NUMBER,
      payload: { page: page - 1 }
    });
    setPage(page - 1);
  };

  const handleOrderDetailsClick = ({ orderId, userId }) => {
    if (isMobile) {
      navigate(`/order/${orderId}`);
      setShowOrder(null);
      setSelectedUserId(null);
    } else {
      setShowOrder(orderId);
      setSelectedUserId(userId);
    }
  };

  const onViewRfiClick = (orderId, rfiType, userId) => {
    const url = `/user/${userId}/order/${orderId}/rfi/${rfiType}`;
    window.open(url, '_blank');
  };

  const renderLoadingState = () => (
    <Flex justify="center" align="center" w="full" flex={1} minH="300px">
      <VStack spacing={3}>
        <Spinner thickness="4px" size="lg" color="brand.400" />
        <Text fontSize="sm" color="whiteAlpha.600">
          Loading RFI transactions...
        </Text>
      </VStack>
    </Flex>
  );

  const renderErrorState = () => (
    <Box
      w="full"
      p={6}
      bg="backGround"
      border="1px solid"
      borderColor="red.300"
      borderRadius="xl"
    >
      <Text color="red.500" fontSize="sm">
        Failed to load RFI transactions. Please try again.
      </Text>
    </Box>
  );

  const renderEmptyState = () => (
    <Flex
      w="full"
      flex={1}
      justify="center"
      align="center"
      minH="300px"
    >
      <Box
        p={10}
        bg="backGround"
        border="1px solid"
        borderColor="whiteAlpha.300"
        borderRadius="xl"
        textAlign="center"
      >
        <Text color="whiteAlpha.700" fontSize="lg">
          No RFI Transactions Found
        </Text>
        <Text color="whiteAlpha.500" fontSize="sm" mt={2}>
          There are currently no RFI transactions to show.
        </Text>
      </Box>
    </Flex>
  );

  const renderRfiList = () => (
    <VStack
      w="full"
      align="flex-start"
      overflow="auto"
      maxH="60vh"
      className="scroll"
      spacing={4}
    >
      {rfis.data.map((data) => (
        <GenericCard
          key={data.referenceId || data.id}
          cardData={data}
          cardType={CARD_TYPE.RFI_FUNNEL_CARD}
          maxW="97%"
          ActionButtons={[
            <Button
              key="order-details-button"
              mt={{ base: 2, md: 0 }}
              ml={{ base: 0, md: 1 }}
              rightIcon={<MdEast color="black" />}
              rounded="lg"
              color="black"
              colorScheme="brand"
              alignSelf="center"
              px={5}
              onClick={() =>
                handleOrderDetailsClick({
                  orderId: data.referenceId,
                  userId: data.userId
                })
              }
            >
              Order details
            </Button>,
            <Button
              key="rfi-details-button"
              mt={{ base: 2, md: 0 }}
              ml={{ base: 0, md: 1 }}
              rightIcon={<MdEast color="black" />}
              rounded="lg"
              color="black"
              colorScheme="brand"
              alignSelf="center"
              px={5}
              onClick={() =>
                onViewRfiClick(data.referenceId, data.rfiType, data.userId)
              }
            >
              RFI Details
            </Button>
          ]}
        />
      ))}
    </VStack>
  );

  const renderContent = () => {
    if (rfis.isLoading) return renderLoadingState();
    if (rfis.isError) return renderErrorState();
    if (!rfis.data?.length) return renderEmptyState();
    return renderRfiList();
  };

  return (
    <ContentLayout>
      <Grid
        gridTemplateColumns={{ base: '1fr', lg: '7fr 0fr 3fr' }}
        h="full"
      >
        <VStack m={4} align="stretch" gap={4} h="full">
          <HStack maxW="80rem">
            <InputComponent
              type="text"
              value={searchValue}
              name="query"
              handleChange={handleSearchChange}
              placeholder="Search RFI by Order ID"
              leftElement={<MdSearch size="2rem" color="white" />}
              width="30rem"
              maxWidth="100%"
              size="md"
              autoComplete="off"
              spellcheck={false}
              autoCorrect="off"
            />
            <Button
              type="submit"
              color="black"
              rounded="lg"
              colorScheme="brand"
              py={2}
              onClick={() => handleSearch()}
              fontWeight={600}
              isLoading={rfis.isLoading}
              alignSelf="start"
              mt={4}
              >
              Search
            </Button>
          </HStack>

          <Flex gap={3} flexWrap="wrap" w="full">
            <Select
              placeholder="Filter Country"
              onChange={handleFilterChange}
              name="countryCode"
              value={filters.countryCode}
              bg="black"
              color="gray"
              size="sm"
              rounded="lg"
              w="auto"
              minW="140px"
              _focus={{
                border: '1px solid #81EBAB',
                boxShadow: 'none'
              }}
            >
              {Object.keys(COUNTRY_CODE).map((status) => (
                <option value={status} key={status}>
                  {status}
                </option>
              ))}
            </Select>
            <Select
              placeholder="Filter RFI Type"
              onChange={handleFilterChange}
              name="type"
              value={filters.type}
              bg="black"
              color="gray"
              size="sm"
              rounded="lg"
              w="auto"
              minW="140px"
              _focus={{
                border: '1px solid #81EBAB',
                boxShadow: 'none'
              }}
            >
              {Object.keys(RFI_TYPES).map((rfiType) => (
                <option value={rfiType} key={rfiType}>
                  {rfiType}
                </option>
              ))}
            </Select>
            <Select
              placeholder="Filter RFI Status"
              onChange={handleFilterChange}
              name="status"
              value={filters.status}
              bg="black"
              color="gray"
              size="sm"
              rounded="lg"
              w="auto"
              minW="140px"
              _focus={{
                border: '1px solid #81EBAB',
                boxShadow: 'none'
              }}
            >
              {Object.keys(RFI_STATUS).map((rfiStatus) => (
                <option value={rfiStatus} key={rfiStatus}>
                  {rfiStatus}
                </option>
              ))}
            </Select>
            <Select
              placeholder="Request Reason Type"
              onChange={handleFilterChange}
              name="requestReason"
              value={filters.requestReason}
              bg="black"
              color="gray"
              size="sm"
              rounded="lg"
              w="auto"
              minW="170px"
              _focus={{
                border: '1px solid #81EBAB',
                boxShadow: 'none'
              }}
            >
              {Object.keys(RFI_REQUEST_REASONS).map((requestReason) => (
                <option value={requestReason} key={requestReason}>
                  {requestReason}
                </option>
              ))}
            </Select>
            <Select
              onChange={handleFilterChange}
              name="sortValue"
              value={filters.sortValue}
              bg="black"
              color="gray"
              size="sm"
              rounded="lg"
              w="auto"
              minW="120px"
              _focus={{
                border: '1px solid #81EBAB',
                boxShadow: 'none'
              }}
            >
              {Object.keys(SORT_OPTIONS).map((sortOption) => (
                <option
                  value={SORT_OPTIONS[sortOption]?.value}
                  key={SORT_OPTIONS[sortOption]?.value}
                >
                  {SORT_OPTIONS[sortOption]?.label}
                </option>
              ))}
            </Select>
            <Select
              placeholder="Pending for all"
              onChange={handleFilterChange}
              name="pendingForHours"
              value={filters.pendingForHours}
              bg="black"
              color="gray"
              size="sm"
              rounded="lg"
              w="auto"
              minW="140px"
              _focus={{
                border: '1px solid #81EBAB',
                boxShadow: 'none'
              }}
            >
              {getPendingForHoursOptions().map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </Flex>

          {!rfis.isLoading && !rfis.isError && rfis.totalPages > 1 && (
            <Pagination
              totalPages={rfis.totalPages}
              currentPage={rfis.page + 1}
              onPageChange={handlePageChange}
            />
          )}

          {renderContent()}
        </VStack>
        <Divider />
        {/*NOTE :: ADD userId prop in order to navigate to userId*/}
        {!isMobile && showOrder && (
          <OrderDetailsCard
            userId={selectedUserId}
            showNavLink={true}
            orderId={showOrder}
            isSidebar
          />
        )}
      </Grid>
    </ContentLayout>
  );
};

export default RFITransactions;
